/**
 * @file useLegalDocActions.ts
 * @description Действия карточки документа: «Сохранить черновик»,
 * «Сохранить и опубликовать» / «Показать снова», «Отменить правки»
 * (phase19.16, SB139, SB143; db-design-legal-docs §4).
 *
 * Каждое действие кладёт в страницу карточку из ответа API (`setDoc` +
 * `form.reset`) — список и поля не правятся руками. Ошибки — toast с
 * текстом по коду (`legalErrorText`); 409 `legal_draft_changed` — с кнопкой
 * «Обновить» (перечитать карточку). Предупреждения публикации (SB120 — не
 * запреты) — отдельными toast.
 */

import { useState } from "react";

import { useNotify } from "../../components/ui/NotifyProvider";
import { discardLegalDraft, publishLegalDoc, updateLegalDoc, type LegalDocumentDto } from "../../lib/api-legal";
import { legalWarningText } from "../../lib/legal/legal-doc-view";
import { legalErrorCode, legalErrorText } from "../../lib/legal/legal-errors";
import type { LegalDraftForm } from "./useLegalDraftForm";

export type LegalBusyAction = "save" | "publish" | "discard";

export interface LegalDocActions {
  busy: LegalBusyAction | null;
  saveDraft: () => Promise<void>;
  publish: () => Promise<void>;
  discard: () => Promise<void>;
  /** Карточка после снятия/правки состава (окна и секции) — та же раскладка в страницу. */
  apply: (doc: LegalDocumentDto) => void;
  /** Показать ошибку API по правилам карточки. */
  fail: (err: unknown, fallback: string) => void;
}

export function useLegalDocActions(
  doc: LegalDocumentDto | null,
  setDoc: (doc: LegalDocumentDto) => void,
  form: LegalDraftForm,
  reload: () => Promise<LegalDocumentDto | null>,
): LegalDocActions {
  const notify = useNotify();
  const [busy, setBusy] = useState<LegalBusyAction | null>(null);

  const apply = (next: LegalDocumentDto) => {
    setDoc(next);
    form.reset(next);
  };

  const fail = (err: unknown, fallback: string) => {
    const text = legalErrorText(err, fallback);
    if (legalErrorCode(err) === "legal_draft_changed") {
      // «Обновить» — свежая карточка и сброс формы на неё (несохранённое уходит: его перезаписал другой).
      notify.warning(text, { action: { label: "Обновить", onClick: () => void reload().then((fresh) => fresh && form.reset(fresh)) } });
    } else {
      notify.error(text);
    }
  };

  /** PATCH несохранённых правок; без правок — карточка как есть. */
  async function saveIfDirty(current: LegalDocumentDto): Promise<LegalDocumentDto> {
    if (!form.dirty) return current;
    return updateLegalDoc(current.id, { expectedVersion: current.version, ...form.patch });
  }

  async function run(action: LegalBusyAction, work: (current: LegalDocumentDto) => Promise<void>, fallback: string) {
    if (!doc || busy) return;
    setBusy(action);
    try {
      await work(doc);
    } catch (err) {
      fail(err, fallback);
    } finally {
      setBusy(null);
    }
  }

  const saveDraft = () =>
    run(
      "save",
      async (current) => {
        apply(await saveIfDirty(current));
        // У группы редакций нет — правка сразу на сайте (решение §7.27 (в) 1).
        notify.success(current.kind === "GROUP" && current.isPublished ? "Сохранено — на сайте уже с изменениями" : "Черновик сохранён");
      },
      "Не удалось сохранить",
    );

  const publish = () =>
    run(
      "publish",
      async (current) => {
        const saved = await saveIfDirty(current);
        // Сохранённое кладём сразу: если публикация упадёт, следующий PATCH пойдёт
        // с новой версией, а не даст ложный 409 «изменён в другом окне».
        if (saved !== current) apply(saved);
        // После сохранения публиковать нечего (менялся только способ показа PDF) — без запроса, иначе 409.
        if (saved.kind !== "GROUP" && saved.isPublished && saved.live && !saved.hasDraftChanges) {
          notify.success("Сохранено. Изменений для публикации нет");
          return;
        }
        // «Показать снова» — тот же адрес publish: снятый без правок сервер показывает той же редакцией.
        const result = await publishLegalDoc(saved.id, saved.version);
        apply(result.document);
        if (result.revision) notify.success(`Опубликована редакция ${result.revision.number}`);
        else notify.success(current.kind === "GROUP" ? "Группа опубликована" : "Документ снова на сайте");
        for (const warning of result.warnings) notify.warning(legalWarningText(warning));
      },
      "Не удалось опубликовать",
    );

  const discard = () =>
    run(
      "discard",
      async (current) => {
        apply(await discardLegalDraft(current.id, current.version));
        notify.success("Правки отменены — черновик равен действующей редакции");
      },
      "Не удалось отменить правки",
    );

  return { busy, saveDraft, publish, discard, apply, fail };
}
